import { HEAD_ABOVE_FEET, type CharacterSource } from "./world-ui/characters.ts";
import type { SessionParts } from "./session-types.ts";
import type { SessionCrossing } from "./session-crossing.ts";

/**
 * Makes the characters the scene draws, as world-attached UI reads them (`world-ui/characters.ts`): the local character
 * where its sprite stands this frame, and every player character and creature drawn, the local one first, with the
 * state the client's view holds for it and how far its art reaches above its feet, a hop's lift included, so the plate
 * rises with the head (ADR-116's third amendment); the player's target; and the world's look table. The nameplates
 * read it; the scene names no surface. It is made once and visits the view through the callback it is given, so
 * reading it allocates nothing.
 *
 * @param parts The session's parts.
 * @param crossing The other maps' figures and the held ones, visited after the owning map's under keys no handle can be (ADR-147).
 * @returns The source.
 */
export function characterSource(parts: SessionParts, crossing: SessionCrossing): CharacterSource {
  const { host, state, connection, local, remotes, targets } = parts;
  return {
    local: (out) => {
      if (local.sprite === null) {
        return false;
      }
      out.x = local.drawn.x;
      out.y = local.drawn.y;
      return true;
    },
    localHandle: () => (local.sprite === null ? -1 : state.handle),
    forEach: (visit) => {
      const current = connection();
      if (current === null) {
        return;
      }
      const own = local.sprite === null ? undefined : current.view.entities.get(state.handle);
      if (own !== undefined) {
        visit(state.handle, local.drawn.x, local.drawn.y, own, local.plateHead(host.ownHead?.() ?? HEAD_ABOVE_FEET), false);
      }
      remotes.forEach(visit, current.view.entities);
      crossing.visit(visit);
    },
    target: () => targets.handle,
    creatureLook: (appearance) => host.world()?.looks[appearance] ?? null,
    npcLook: (appearance) => host.world()?.npcLooks[appearance] ?? null,
  };
}
